import { makeAutoObservable, runInAction } from "mobx";
import { IReadingWord, IRootStore, IScoreRequest, ITextEntity, SiddurNode, SiddurTree, WordStatus } from "@shared/types";
import { getSiddurTree } from "../../services/api/siddur.service";
import { createTextEntity } from "../../utils/text-entities-formater.utils";
import { stripSeparationMarks, toHeavySheva } from "../../utils/hebrew-marks";

/**
 * A unit ready to be put on a screen.
 *
 * `text` is the whole unit, which is what a reading gets compared against and
 * what a score is written for. `lines` is the same words split where a reader
 * breathes, which is only how it looks. Splitting the display never splits the
 * score.
 */
export interface SiddurUnit {
  node: SiddurNode;
  /** The prayer this unit sits in. Screens never group across two of these. */
  parentId: number;
  /**
   * The name of that prayer.
   *
   * A unit has no name, only a place in order. Without this the seven זמירות
   * of ליל שבת run one into the next with nothing on screen to say which one
   * you are reading, and 187 units look like a single long song.
   */
  prayerName: string;
  lines: string[];
  syllables: number;
}

/** One screen: the units a reader takes in one go. */
export interface SiddurScreen {
  units: SiddurUnit[];
  syllables: number;
  /** The prayer these units belong to; a screen never spans two. */
  prayerName: string;
}


/** מפתח שמירת מספר היחידות במסך ב-localStorage */
const UNITS_STORAGE_KEY = "shanenli:siddur-units";

const loadUnitsPerScreen = (): UnitsPerScreen => {
  try {
    const n = Number(localStorage.getItem(UNITS_STORAGE_KEY));
    return n === 2 || n === 3 ? n : 1;
  } catch (error) {
    return 1;
  }
};

const saveUnitsPerScreen = (count: UnitsPerScreen): void => {
  try {
    localStorage.setItem(UNITS_STORAGE_KEY, String(count));
  } catch (error) {
    // אחסון חסום - הבחירה נשמרת לזמן הסשן בלבד
  }
};

/** מפתח שמירת המקום בסידור ב-localStorage */
const PLACE_STORAGE_KEY = "shanenli:siddur-place";

/**
 * Where the reader was: the crumbs as node ids, and the first unit on the
 * screen they were looking at. Ids, not indexes, so a corpus edit that adds
 * or removes a verse cannot land them on the wrong page.
 */
interface SavedPlace {
  pathIds: number[];
  anchor?: number;
}

const loadPlace = (): SavedPlace | null => {
  try {
    const raw = localStorage.getItem(PLACE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed?.pathIds) || !parsed.pathIds.every((id: unknown) => typeof id === "number")) return null;
    return { pathIds: parsed.pathIds, anchor: typeof parsed.anchor === "number" ? parsed.anchor : undefined };
  } catch (error) {
    return null;
  }
};

const savePlace = (place: SavedPlace): void => {
  try {
    localStorage.setItem(PLACE_STORAGE_KEY, JSON.stringify(place));
  } catch (error) {
    // אחסון חסום - המקום נשמר לזמן הסשן בלבד
  }
};

/**
 * Re-flow a prayer's lines so that a sentence never continues past its own
 * period. Metzuda's lines follow the printed page, where a line can end with
 * the first word of the next sentence: ידיד נפש has "נָגִילָה וְנִשְׂמְחָה בָּךְ. מַהֵר"
 * on one line and "אָהוּב כִּי בָא מוֹעֵד" on the next. On a screen that reads
 * as a sentence ending in מהר. So a line is cut at a period or colon that has
 * words after it, and the words after it open the following line. 45 of the
 * 3909 lines in the Siddur file are affected.
 */
export function breakLinesAtPeriods(lines: string[]): string[] {
  const out: string[] = [];
  let carry = '';
  for (const line of lines) {
    let rest = carry ? `${carry} ${line}` : line;
    carry = '';
    // A sentence end followed by more words on the same line.
    let cut = false;
    let m: RegExpMatchArray | null;
    while ((m = rest.match(/^(.*?[.:\u05C3])\s+(\S.*)$/))) {
      out.push(m[1]);
      rest = m[2];
      cut = true;
    }
    // What follows the last cut opens the next line; an uncut line stays whole.
    if (cut) carry = rest;
    else if (rest) out.push(rest);
  }
  if (carry) out.push(carry);
  return out;
}


export type UnitsPerScreen = 1 | 2 | 3;

export type SiddurReadingMode = "reading" | "byHeart";

/**
 * The screen as the speech pipeline wants it, plus where each unit ends.
 *
 * The pipeline compares one flat list of words. A screen can hold three units,
 * and each of them is scored on its own, so we keep the boundary between them:
 * `wordCount` is how many of the screen's clean words belong to that unit, in
 * order. The entity is built by joining the unit entities rather than cleaning
 * the whole screen at once, so the counts cannot drift from the words.
 */
export interface SiddurScreenReading {
  entity: ITextEntity;
  units: { nodeId: number; wordCount: number }[];
}

class SiddurController {
  rootStore: IRootStore;

  tree: SiddurTree | null = null;
  version = "";
  isLoading = false;
  error: string | null = null;

  /** Root first, then each chosen child. The breadcrumb is this list. */
  path: SiddurNode[] = [];
  /** How many units share a screen. The reader's choice, kept on the device. */
  unitsPerScreen: UnitsPerScreen = loadUnitsPerScreen();
  screenIndex = 0;

  /** True from the moment the reader presses a reading button until they leave. */
  isReading = false;
  /** With the words on screen, or from memory. */
  readingMode: SiddurReadingMode = "reading";
  /** Which read of the current screen this is, for reads-per-verse in the flow. */
  readIndex = 1;
  /** Bumped on every re-read so the reading view remounts and listens afresh. */
  readRun = 0;
  /** This session's latest score per unit, so the screen shows how each went. */
  sessionScores: Record<number, number> = {};

  constructor(rootStore: IRootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  load = async (): Promise<void> => {
    if (this.tree || this.isLoading) return;
    runInAction(() => {
      this.isLoading = true;
      this.error = null;
    });
    try {
      const { version, data } = await getSiddurTree();
      runInAction(() => {
        this.tree = data;
        this.version = version;
        this.path = [data.root];
        this.screenIndex = 0;
        this.restorePlace(data.root);
      });
    } catch (e) {
      console.error("Failed to load the Siddur tree:", e);
      runInAction(() => {
        this.error = "לא הצלחנו לטעון את הסידור";
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  /** The node whose text is on screen: the last crumb the reader chose. */
  get currentNode(): SiddurNode | null {
    return this.path[this.path.length - 1] ?? null;
  }

  /** What the reader can drill into from here. Empty once we are at units. */
  get childBranches(): SiddurNode[] {
    return (this.currentNode?.children ?? []).filter((child) => !!child.children);
  }

  /** Every unit under the current node, in the order they are said. */
  get units(): SiddurUnit[] {
    const out: SiddurUnit[] = [];
    const walk = (node: SiddurNode, parentId: number, prayerName: string): void => {
      if (node.children) {
        // The nearest named ancestor is the prayer, whatever the depth.
        const name = node.name_he || prayerName;
        node.children.forEach((child) => walk(child, node.id, name));
        return;
      }
      if (!node.text) return; // A node with neither cannot exist, but never render one.
      out.push({
        node,
        parentId,
        prayerName,
        lines: breakLinesAtPeriods(node.lines?.length ? node.lines : [node.text]).map((line) => toHeavySheva(stripSeparationMarks(line))),
        syllables: node.syllables ?? 0,
      });
    };
    if (this.currentNode) {
      walk(this.currentNode, this.currentNode.id, this.currentNode.name_he ?? '');
    }
    return out;
  }

  /**
   * The units grouped the way the reader asked for them.
   *
   * A group closes early on two things: the next unit belongs to a different
   * prayer, or adding it would push the screen past the ceiling. Either way the
   * reader never gets half of one prayer sharing a screen with the start of the
   * next.
   */
  get screens(): SiddurScreen[] {
    const out: SiddurScreen[] = [];
    let current: SiddurUnit[] = [];
    let syllables = 0;

    const close = (): void => {
      if (current.length) {
        out.push({ units: current, syllables, prayerName: current[0].prayerName });
      }
      current = [];
      syllables = 0;
    };

    for (const unit of this.units) {
      // Units per screen means exactly that: the count the reader chose, and
      // a screen never crosses a prayer, so it carries one prayer name. There
      // is no syllable ceiling: a long screen scrolls inside its box.
      const sameParent = current.length === 0 || current[0].parentId === unit.parentId;
      if (current.length >= this.unitsPerScreen || !sameParent) close();
      current.push(unit);
      syllables += unit.syllables;
    }
    close();
    return out;
  }

  get currentScreen(): SiddurScreen | null {
    return this.screens[this.screenIndex] ?? null;
  }

  /**
   * The current screen turned into something the speech pipeline can read.
   *
   * This is the whole seam. The pipeline already knows how to compare a
   * recording against an ITextEntity; it does not need to know that this one
   * came from a tree instead of a Sections row.
   */
  get currentScreenReading(): SiddurScreenReading | null {
    const screen = this.currentScreen;
    if (!screen) return null;

    const perUnit = screen.units.map((unit) =>
      createTextEntity({
        index: 0,
        id: unit.node.id,
        label: unit.node.name_he ?? screen.prayerName,
        htmlTextLines: unit.lines,
      })
    );

    if (perUnit.every((entity) => entity.cleanWordsArray.length === 0)) return null;

    return {
      entity: {
        index: this.screenIndex,
        id: screen.units[0].node.id,
        label: screen.prayerName,
        htmlTextLines: perUnit.flatMap((entity) => entity.htmlTextLines),
        cleanedText: perUnit.map((entity) => entity.cleanedText).join(" ").trim(),
        cleanWordsArray: perUnit.flatMap((entity) => entity.cleanWordsArray),
        originalWordsArray: perUnit.flatMap((entity) => entity.originalWordsArray),
      },
      units: screen.units.map((unit, i) => ({
        nodeId: unit.node.id,
        wordCount: perUnit[i].cleanWordsArray.length,
      })),
    };
  }

  startReading = (mode: SiddurReadingMode): void => {
    this.readingMode = mode;
    this.isReading = true;
    this.readIndex = 1;
  };

  /** Read the same screen once more, in the continuous flow. */
  readAgain = (): void => {
    this.readIndex += 1;
    this.readRun += 1;
  };

  stopReading = (): void => {
    this.isReading = false;
  };

  /**
   * One score per unit, each from its own words.
   *
   * A three-unit screen is three readings that happened to share a screen. One
   * score across all three would tell a student their אשרי was 82% without
   * saying which of the three they actually stumbled in, and the unit is the
   * thing the database can point at.
   */
  saveScreenScores = async (timing: {
    executionTime: string;
    wordsPerMinute: number;
  }): Promise<void> => {
    const reading = this.currentScreenReading;
    if (!reading) return;

    const words = this.rootStore.stores.readingSessionStore.allWords ?? [];
    const userStore = this.rootStore.stores.userStore;

    // The user store reports a failed save by setting its error rather than
    // throwing, so a caller that only awaits it cannot tell a saved reading
    // from a lost one. Clear it first, and raise afterwards if it came back.
    userStore.setError?.(null);

    let at = 0;
    for (const unit of reading.units) {
      const slice = words.slice(at, at + unit.wordCount);
      at += unit.wordCount;
      if (unit.wordCount === 0) continue;

      // Read is read: correct, nearly right, or swallowed into its neighbour,
      // the same count the reading stats and the book reader use.
      const read = slice.filter(
        (word: IReadingWord) =>
          word.status === WordStatus.Correct ||
          word.status === WordStatus.Near ||
          word.status === WordStatus.Coarticulated
      ).length;
      const score = Math.round((read / unit.wordCount) * 100);

      await userStore.saveUserScore({
        siddurNodeId: unit.nodeId,
        contentVersion: this.version,
        score,
        isByHeart: this.readingMode === "byHeart",
        executionTime: timing.executionTime,
        wordsPerMinute: timing.wordsPerMinute,
        // Stars grow by this unit's words, not the whole screen's per unit.
        rankWords: read,
      } as IScoreRequest);

      if (userStore.error) throw new Error(userStore.error);
      runInAction(() => {
        this.sessionScores = { ...this.sessionScores, [unit.nodeId]: score };
      });
    }
  };

  /**
   * Come back to where the reader left off: the same crumbs, the screen that
   * holds the same first unit. Any crumb that no longer exists (the corpus
   * moved on) drops the rest of the trail and the reader lands on its parent.
   */
  private restorePlace(root: SiddurNode): void {
    const saved = loadPlace();
    if (!saved || saved.pathIds[0] !== root.id) return;
    const path: SiddurNode[] = [root];
    for (const id of saved.pathIds.slice(1)) {
      const child = (path[path.length - 1].children ?? []).find((c) => c.id === id);
      if (!child) break;
      path.push(child);
    }
    this.path = path;
    const at = saved.anchor === undefined ? -1 : this.screens.findIndex((s) => s.units.some((u) => u.node.id === saved.anchor));
    this.screenIndex = at >= 0 ? at : 0;
  }

  private rememberPlace(): void {
    savePlace({
      pathIds: this.path.map((node) => node.id),
      anchor: this.currentScreen?.units[0]?.node.id,
    });
  }

  /** Drill into a child. The reader starts at the top of what they picked. */
  goToChild = (child: SiddurNode): void => {
    this.path = [...this.path, child];
    this.screenIndex = 0;
    this.rememberPlace();
  };

  /** Click a crumb: everything after it falls away. */
  goToCrumb = (depth: number): void => {
    this.path = this.path.slice(0, depth + 1);
    this.screenIndex = 0;
    this.rememberPlace();
  };

  /**
   * Change how many units share a screen, keeping the reader where they are.
   *
   * Without the anchor, a reader deep in אשרי who switches from one unit to
   * three lands back near the beginning, because screen 40 of one size is
   * nowhere near screen 40 of another.
   */
  setUnitsPerScreen = (count: UnitsPerScreen): void => {
    const anchor = this.currentScreen?.units[0]?.node.id;
    this.unitsPerScreen = count;
    saveUnitsPerScreen(count);
    const at = anchor === undefined ? -1 : this.screens.findIndex((s) => s.units.some((u) => u.node.id === anchor));
    this.screenIndex = at >= 0 ? at : 0;
    this.rememberPlace();
  };

  goToScreen = (index: number): void => {
    this.screenIndex = Math.min(Math.max(index, 0), Math.max(this.screens.length - 1, 0));
    this.readIndex = 1;
    this.rememberPlace();
  };

  next = (): void => this.goToScreen(this.screenIndex + 1);
  previous = (): void => this.goToScreen(this.screenIndex - 1);

  get hasNext(): boolean {
    return this.screenIndex < this.screens.length - 1;
  }

  get hasPrevious(): boolean {
    return this.screenIndex > 0;
  }
}

export default SiddurController;
